import { useState } from 'react';

import { Button, Chip, IconButton, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AddRounded as AddIcon, DeleteRounded as DeleteIcon, EditRounded as EditIcon } from '@material-ui/icons';

import { UserAddress } from '../../types/user.type';
import { AdminUpdateUserAddressDTO } from '../../types/dtos/admin-update-user.dto';
import { UpdateUserFields } from '../../hooks/use-user-edit-form.hook';
import UserAddressDialog from '../Modals/UserAddressDialog.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
    maxHeight: '55svh',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  lockHint: {
    fontSize: 12,
    color: '#757575',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    overflowY: 'auto',
    paddingRight: '0.25rem',
  },
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    minWidth: 0,

    '& > span': {
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  cardBody: {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    color: '#424242',
    whiteSpace: 'pre-line',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',

    '& .MuiChip-root': {
      borderRadius: 4,
      fontWeight: 600,
      fontSize: 11,
      height: 18,

      '& .MuiChip-label': {
        paddingLeft: 6,
        paddingRight: 6,
      },
    },
  },
  deliveryChip: {
    backgroundColor: theme.palette.admin.primary.extraLight,
    color: theme.palette.admin.primary.extraDark,
  },
  billingChip: {
    backgroundColor: theme.palette.admin.secondary.extraLight,
    color: theme.palette.admin.secondary.extraDark,
  },
}));

type AddressDialogState = { isOpen: boolean; address: UserAddress | null };

const CLOSED_ADDRESS_DIALOG: AddressDialogState = { isOpen: false, address: null };

const LOCK_HINT = 'Enregistrez ou annulez vos modifications pour gérer les adresses';

const addressFlagRank = (address: UserAddress) => (address.isMain ? 2 : 0) + (address.isBill ? 1 : 0);

const describeAddress = (address: UserAddress) =>
  [
    `${address.street} (${address.zipCode} - ${address.city})`,
    address.comment ? `Note : ${address.comment}` : undefined,
  ]
    .filter(Boolean)
    .join('\n');

interface Props {
  addresses: UserAddress[];
  isUpdating: boolean;
  isLocked: boolean;
  updateUser: UpdateUserFields;
}

export function UserAddressesCard({ addresses, isUpdating, isLocked, updateUser }: Props) {
  const classes = useStyles();

  const [addressDialog, setAddressDialog] = useState<AddressDialogState>(CLOSED_ADDRESS_DIALOG);
  const [addressToDelete, setAddressToDelete] = useState<UserAddress | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isSubmitting = isUpdating || isSaving;
  const areActionsDisabled = isSubmitting || isLocked;

  const sortedAddresses = [...addresses].sort((first, second) => addressFlagRank(second) - addressFlagRank(first));

  const saveAddresses = async (nextAddresses: AdminUpdateUserAddressDTO[]) => {
    setIsSaving(true);
    try {
      return await updateUser({ addresses: nextAddresses });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddressSave = async (savedAddress: AdminUpdateUserAddressDTO) => {
    const isExisting = addresses.some((address) => address.id === savedAddress.id);
    const nextAddresses: AdminUpdateUserAddressDTO[] = isExisting
      ? addresses.map((address) => (address.id === savedAddress.id ? savedAddress : address))
      : [...addresses, savedAddress];

    const updatedUser = await saveAddresses(nextAddresses);
    if (updatedUser) setAddressDialog(CLOSED_ADDRESS_DIALOG);
  };

  const handleAddressDeleteConfirm = async () => {
    if (!addressToDelete) return;

    const updatedUser = await saveAddresses(addresses.filter((address) => address.id !== addressToDelete.id));
    if (updatedUser) setAddressToDelete(null);
  };

  return (
    <section>
      <h4>Adresses</h4>
      <Paper className={classes.paper}>
        <div className={classes.header}>
          {isLocked && <span className={classes.lockHint}>{LOCK_HINT}</span>}
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={areActionsDisabled}
            onClick={() => setAddressDialog({ isOpen: true, address: null })}
          >
            Ajouter
          </Button>
        </div>
        <div className={classes.list}>
          {sortedAddresses.length === 0 && <span>Aucune adresse enregistrée pour cet utilisateur.</span>}
          {sortedAddresses.map((address) => (
            <div key={address.id} className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardTitle}>
                  <span>{address.title || address.street}</span>
                  <div className={classes.chips}>
                    {address.isMain && <Chip size="small" className={classes.deliveryChip} label="Livraison" />}
                    {address.isBill && <Chip size="small" className={classes.billingChip} label="Facturation" />}
                  </div>
                </div>
                <div className={classes.cardActions}>
                  <IconButton
                    size="small"
                    disabled={areActionsDisabled}
                    title="Modifier l'adresse"
                    onClick={() => setAddressDialog({ isOpen: true, address })}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={areActionsDisabled}
                    title="Supprimer l'adresse"
                    onClick={() => setAddressToDelete(address)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              <span className={classes.cardBody}>{describeAddress(address)}</span>
            </div>
          ))}
        </div>
      </Paper>

      <UserAddressDialog
        isOpen={addressDialog.isOpen}
        address={addressDialog.address}
        isSubmitting={isSubmitting}
        onClose={() => setAddressDialog(CLOSED_ADDRESS_DIALOG)}
        onSave={handleAddressSave}
      />
      {addressToDelete && (
        <ConfirmActionDialog
          open
          title="Supprimer l'adresse"
          description={`L'adresse « ${addressToDelete.street}, ${addressToDelete.city} » sera supprimée.`}
          confirmLabel="Supprimer"
          isSubmitting={isSubmitting}
          onConfirm={handleAddressDeleteConfirm}
          onClose={() => setAddressToDelete(null)}
        />
      )}
    </section>
  );
}
